/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Service for Qinaya-specific telemetry tracking, including connection
 * failure tracking and session summary reporting.
 * 
 * ARCHITECTURE: Passive Observer Pattern
 * 
 * This service observes Guacamole's existing session lifecycle as the source of truth.
 * It does NOT manage or alter session ownership or lifecycle decisions.
 * 
 * Integration Points:
 * - ManagedClient.js is the single source of truth for session lifecycle
 * - This service hooks into ManagedClient state changes in a passive, observer-style manner
 * - Uses Guacamole's connectionId/tunnel identifiers as session identity (source of truth)
 * 
 * Design Principles:
 * 1. Single Responsibility: Only records metrics, never manages session state
 * 2. Idempotency: All lifecycle methods are idempotent (safe to call multiple times)
 * 3. No Double-Counting: Guards prevent duplicate teardown events or metric counting
 * 4. Composable: Works alongside Guacamole's existing session tracking without competing
 * 
 * Session Lifecycle Hooks (all passive observers):
 * - startSession(): Called when Guacamole state becomes CONNECTED (state 3)
 * - endSession(): Called when Guacamole state becomes DISCONNECTING/DISCONNECTED (states 4/5) or tunnel CLOSED
 * - recordDisconnection(): Records disconnection events without altering session state
 * - recordConnectionLoss(): Records connection losses (e.g., tunnel UNSTABLE) without altering session state
 * - recordFailure(): Records connection failures without altering session state
 */
angular.module('client').factory('qinayaTelemetryService', ['$injector',
    function qinayaTelemetryService($injector) {

    // Get authenticationService for API requests
    var authenticationService = null;
    try {
        authenticationService = $injector.get('authenticationService');
    } catch (e) {
        // authenticationService may not be available in all contexts
    }

    const service = {};

    /**
     * Placeholder URL for reporting repeated connection failures.
     * @constant
     * @type {String}
     */
    const QINAYA_FAILURE_URL = 'https://example.invalid/qinaya/guac/failure';

    /**
     * Placeholder URL for posting session summary telemetry.
     * @constant
     * @type {String}
     */
    const QINAYA_SESSION_SUMMARY_URL = 'https://example.invalid/qinaya/guac/session-summary';

    /**
     * Number of consecutive failures required before sending telemetry.
     * @constant
     * @type {Number}
     */
    const FAILURE_THRESHOLD = 3;

    /**
     * Provisional live rating formula version. Must stay aligned with
     * TelemetryManager.FORMULA_VERSION ("backend-penalty-v1").
     * @constant
     * @type {String}
     */
    const TELEMETRY_RATING_FORMULA_VERSION = 'backend-penalty-v1';

    /**
     * Returns true when Mirage telemetry debug logging is enabled. Telemetry
     * diagnostics are ON by default so that "[Telemetry]" session lifecycle
     * logs are visible in the browser console without extra setup. To silence
     * them at runtime, set window.GUAC_DEBUG_TELEMETRY = false (or
     * window.GUAC_DEBUG_LATENCY = false).
     */
    function isTelemetryDebugEnabled() {
        return window.GUAC_DEBUG_TELEMETRY !== false
            && window.GUAC_DEBUG_LATENCY !== false;
    }

    /**
     * Logs normal telemetry diagnostics only when debug mode is enabled.
     */
    function telemetryDebugLog() {
        if (isTelemetryDebugEnabled())
            console.log.apply(console, arguments);
    }

    /**
     * Map of connectionId to consecutive failure count.
     * @private
     * @type {Object.<String, Number>}
     */
    const failureCounts = {};

    /**
     * Map of connectionId to whether the failure threshold notification
     * has already been sent (to prevent duplicate notifications).
     * @private
     * @type {Object.<String, Boolean>}
     */
    const failureNotificationSent = {};

    /**
     * Map of sessionId to session metrics currently being tracked.
     * This allows multiple sessions with the same connectionId to coexist.
     * @private
     * @type {Object.<String, Object>}
     */
    const sessionMetrics = {};
    
    /**
     * Map of connectionId to the current active sessionId for that connection.
     * This allows lookup of the current session for a connection.
     * @private
     * @type {Object.<String, String>}
     */
    const activeSessionsByConnection = {};

    /**
     * Map of Guacamole tunnel UUID to our sessionId.
     * This provides a mapping from Guacamole's native session identifier (tunnel UUID)
     * to our internal session tracking ID.
     * @private
     * @type {Object.<String, String>}
     */
    const tunnelUuidToSessionId = {};

    /**
     * Map of connectionId to a tunnel UUID that was observed before the session
     * for that connection was registered. Guacamole assigns the tunnel UUID
     * (Guacamole.Tunnel.onuuid) while the connection is being established, which
     * is before the client reaches the CONNECTED state where startSession() is
     * invoked. Buffering the UUID here lets startSession() reconcile it so the
     * canonical tunnel UUID (the backend's Mirage session ID) is sent to the
     * backend instead of a provisional local ID.
     * @private
     * @type {Object.<String, String>}
     */
    const pendingTunnelUuidByConnection = {};

    /**
     * Applies any tunnel UUID that was buffered (observed before the session
     * started) to the given session, establishing the canonical mapping.
     *
     * @param {String} connectionId
     *     The connection identifier the tunnel UUID was observed for.
     *
     * @param {String} sessionId
     *     The internal session ID to associate the buffered tunnel UUID with.
     */
    function reconcilePendingTunnelUuid(connectionId, sessionId) {
        var pendingUuid = pendingTunnelUuidByConnection[connectionId];
        if (!pendingUuid || !sessionMetrics[sessionId])
            return;

        sessionMetrics[sessionId].tunnelUuid = pendingUuid;
        tunnelUuidToSessionId[pendingUuid] = sessionId;
        delete pendingTunnelUuidByConnection[connectionId];

        telemetryDebugLog('[Telemetry] Reconciled buffered tunnel UUID - connectionId:', connectionId, 'sessionId:', sessionId, 'tunnelUuid:', pendingUuid);
    }

    /**
     * Records a connection failure for the given connectionId. If this is
     * the third consecutive failure, a telemetry notification is sent.
     *
     * @param {String} connectionId
     *     The identifier of the connection that failed.
     */
    service.recordFailure = function recordFailure(connectionId) {
        if (!connectionId)
            return;

        // Increment failure count for this connection
        failureCounts[connectionId] = (failureCounts[connectionId] || 0) + 1;

        // If we've hit the threshold and haven't already notified, send telemetry
        if (failureCounts[connectionId] === FAILURE_THRESHOLD && !failureNotificationSent[connectionId]) {
            service.notifyFailureThreshold(connectionId);
            failureNotificationSent[connectionId] = true;
        }
    };

    /**
     * Resets the failure count for the given connectionId, typically called
     * after a successful connection.
     *
     * @param {String} connectionId
     *     The identifier of the connection to reset.
     */
    service.resetFailures = function resetFailures(connectionId) {
        if (!connectionId)
            return;

        failureCounts[connectionId] = 0;
        failureNotificationSent[connectionId] = false;
    };

    /**
     * Sends a telemetry notification indicating that the given connection
     * has failed the threshold number of consecutive times.
     *
     * @param {String} connectionId
     *     The identifier of the connection that has repeatedly failed.
     */
    service.notifyFailureThreshold = function notifyFailureThreshold(connectionId) {
        if (!connectionId)
            return;

        const url = QINAYA_FAILURE_URL + '?connectionId=' + encodeURIComponent(connectionId);

        // Send POST request; ignore any errors (no UI impact)
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ connectionId: connectionId, failureCount: FAILURE_THRESHOLD })
        }).catch(function ignoreError() {
            // Silently ignore errors
        });
    };

    /**
     * Generates a unique session ID (UUID v4).
     *
     * @returns {String}
     *     A unique session identifier.
     */
    function generateSessionId() {
        // Generate UUID v4
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0;
            var v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Observes the assignment of a Guacamole tunnel UUID to a session.
     * This is Guacamole's native session identifier and should be used as the source of truth
     * for session identity. This method links the tunnel UUID to our internal sessionId.
     *
     * @param {String} connectionId
     *     The application-level connection identifier.
     * 
     * @param {String} tunnelUuid
     *     The Guacamole tunnel UUID (from Guacamole.Tunnel.onuuid event).
     */
    service.observeTunnelUuid = function observeTunnelUuid(connectionId, tunnelUuid) {
        if (!connectionId || !tunnelUuid)
            return;

        // Get the current active session for this connection
        var sessionId = getCurrentSessionId(connectionId);
        var metrics = sessionId ? sessionMetrics[sessionId] : null;
        if (!metrics) {
            // onuuid can (and normally does) fire before startSession(), which
            // runs when the client reaches CONNECTED. Buffer the UUID so it is
            // reconciled once the session is registered, rather than dropping it.
            pendingTunnelUuidByConnection[connectionId] = tunnelUuid;
            telemetryDebugLog('[Telemetry] Buffered tunnel UUID before session start - connectionId:', connectionId, 'tunnelUuid:', tunnelUuid);
            return;
        }

        // Store the tunnel UUID in the session metrics
        metrics.tunnelUuid = tunnelUuid;
        
        // Create bidirectional mapping
        tunnelUuidToSessionId[tunnelUuid] = sessionId;

        telemetryDebugLog('[Telemetry] Tunnel UUID observed - connectionId:', connectionId, 'sessionId:', sessionId, 'tunnelUuid:', tunnelUuid);
    };

    /**
     * Observes a native Guacamole ping response to calculate round-trip time.
     * This uses Guacamole's built-in ping/pong mechanism for accurate latency measurement.
     * 
     * Expected to be called when the tunnel's oninstruction handler receives a "ping" response
     * with Guacamole.Tunnel.INTERNAL_DATA_OPCODE.
     *
     * @param {String} connectionIdOrSessionId
     *     The identifier of the connection or session.
     * 
     * @param {Number} sentTimestamp
     *     The timestamp when the ping was sent (from the ping message).
     */
    service.observeNativePing = function observeNativePing(connectionIdOrSessionId, sentTimestamp) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved || !sentTimestamp)
            return;

        var metrics = resolved.metrics;
        
        // Calculate round-trip time
        var rtt = Date.now() - sentTimestamp;
        
        // Record the latency sample using the standard recordLatency method
        service.recordLatency(resolved.sessionId, rtt);
        
        // Mark that we're using native ping for this session
        metrics.useNativePing = true;

        telemetryDebugLog('[Telemetry] Native ping RTT:', rtt, 'ms for sessionId:', resolved.sessionId);
    };

    /**
     * Gets the current active sessionId for a connectionId.
     * 
     * @param {String} connectionId
     *     The connection identifier.
     * 
     * @returns {String}
     *     The current active sessionId, or null if no active session.
     */
    function getCurrentSessionId(connectionId) {
        return activeSessionsByConnection[connectionId] || null;
    }

    /**
     * Resolves a connectionId or sessionId to the actual sessionId and metrics.
     * This allows functions to accept either identifier and work correctly.
     * 
     * @param {String} connectionIdOrSessionId
     *     Either a connectionId or sessionId.
     * 
     * @returns {Object}
     *     Object with {sessionId: String, metrics: Object} or null if not found.
     */
    function resolveSession(connectionIdOrSessionId) {
        if (!connectionIdOrSessionId)
            return null;
        
        // First, try as sessionId (direct lookup)
        if (sessionMetrics[connectionIdOrSessionId]) {
            return {
                sessionId: connectionIdOrSessionId,
                metrics: sessionMetrics[connectionIdOrSessionId]
            };
        }
        
        // If not found, try as connectionId (get current active session)
        var sessionId = getCurrentSessionId(connectionIdOrSessionId);
        if (sessionId && sessionMetrics[sessionId]) {
            return {
                sessionId: sessionId,
                metrics: sessionMetrics[sessionId]
            };
        }
        
        return null;
    }

    /**
     * Observes session start for the given connectionId.
     * This is called passively when Guacamole's session tracking indicates a session has started.
     * Uses Guacamole's connectionId as the source of truth for session identity.
     * 
     * This method is idempotent - if a session already exists for this connectionId, it returns
     * the existing sessionId without creating a duplicate.
     *
     * @param {String} connectionId
     *     The identifier of the connection whose session is starting (from Guacamole's session tracking).
     * 
     * @returns {String}
     *     The unique sessionId for this session (either newly created or existing).
     */
    service.startSession = function startSession(connectionId) {
        if (!connectionId)
            return null;

        // Check if we already have an active session for this connection
        // This prevents duplicate session creation if called multiple times
        var existingSessionId = getCurrentSessionId(connectionId);
        if (existingSessionId && sessionMetrics[existingSessionId]) {
            var existingMetrics = sessionMetrics[existingSessionId];
            // Only allow restart if the session has ended
            if (!existingMetrics.sessionEndTime) {
                // Session already exists and is active - return existing sessionId
                // Apply a tunnel UUID that may have been buffered before start.
                reconcilePendingTunnelUuid(connectionId, existingSessionId);
                telemetryDebugLog('[Telemetry] Session already exists for connectionId:', connectionId, 'sessionId:', existingSessionId);
                return existingSessionId;
            }
        }

        // Generate a unique session ID for this session
        var sessionId = generateSessionId();
        telemetryDebugLog('[Telemetry] Observing new session - connectionId:', connectionId, 'sessionId:', sessionId);

        // Store metrics keyed by sessionId (not connectionId) to allow multiple sessions per connection
        sessionMetrics[sessionId] = {
            sessionId: sessionId,              // Unique session identifier (ours)
            connectionId: connectionId,       // Connection identifier from Guacamole (source of truth)
            tunnelUuid: null,                 // Guacamole's native tunnel UUID (set when available)
            sessionStartTime: new Date().toISOString(),
            sessionEndTime: null,
            connectionLossesCount: 0,
            disconnectionCount: 0,           // Total disconnections
            latencySamples: [],
            avgLatencyMs: null,
            lastLatencyMs: null,
            minLatencyMs: null,
            maxLatencyMs: null,
            jitterMs: null,
            latencySpikesCount: 0,
            bytesSent: 0,
            bytesReceived: 0,
            // Bandwidth rate tracking (sliding window for last second)
            sentBytesWindow: [],      // Array of {timestamp, bytes}
            receivedBytesWindow: [],  // Array of {timestamp, bytes}
            // Packet loss tracking
            nopMessagesSent: 0,       // Count of NOP messages sent
            syncResponsesReceived: 0, // Count of sync responses received
            packetLossPercent: null,
            // Guacamole native ping tracking (for more accurate latency)
            pingTimestamps: {},               // Map of ping timestamps for RTT calculation
            useNativePing: false              // Whether to use Guacamole's native ping mechanism
        };
        
        // Update the mapping of connectionId to current active sessionId
        activeSessionsByConnection[connectionId] = sessionId;

        // If the tunnel UUID was already observed (onuuid fires before the
        // client reaches CONNECTED), reconcile it now so the canonical Mirage
        // session ID is what gets sent to the backend.
        reconcilePendingTunnelUuid(connectionId, sessionId);

        return sessionId;
    };

    /**
     * Observes session end for the given connectionId or sessionId.
     * This is called passively when Guacamole's session tracking indicates a session has ended.
     * 
     * This method is idempotent - if a session is already ended, subsequent calls are ignored.
     *
     * @param {String} connectionIdOrSessionId
     *     The identifier of the connection (will use current session) or session (direct lookup).
     */
    service.endSession = function endSession(connectionIdOrSessionId) {
        if (!connectionIdOrSessionId)
            return;
        
        // Try to find session - first try as sessionId, then as connectionId
        var sessionId = connectionIdOrSessionId;
        var metrics = sessionMetrics[sessionId];
        
        // If not found by sessionId, try as connectionId to get current active session
        if (!metrics) {
            sessionId = getCurrentSessionId(connectionIdOrSessionId);
            if (sessionId) {
                metrics = sessionMetrics[sessionId];
            }
        }
        
        if (!metrics)
            return;

        // Idempotent: If session is already ended, don't process again
        if (metrics.sessionEndTime) {
            telemetryDebugLog('[Telemetry] Session already ended - connectionId:', metrics.connectionId, 'sessionId:', metrics.sessionId);
            return;
        }

        telemetryDebugLog('[Telemetry] Observing session end - connectionId:', metrics.connectionId, 'sessionId:', metrics.sessionId);
        metrics.sessionEndTime = new Date().toISOString();
        telemetryDebugLog('[Telemetry] Session end time set to:', metrics.sessionEndTime);

        // Calculate average latency from collected samples
        const samples = metrics.latencySamples;
        if (samples.length > 0) {
            const sum = samples.reduce((a, b) => a + b, 0);
            metrics.avgLatencyMs = Math.round(sum / samples.length);
        }
        
        // Immediately sync to backend to ensure end time is saved
        if (authenticationService) {
            telemetryDebugLog('[Telemetry] Syncing session end to backend - sessionId:', metrics.sessionId);
            service.syncSessionToBackend(sessionId);
        }
        
        // Remove from active sessions mapping
        if (activeSessionsByConnection[metrics.connectionId] === sessionId) {
            delete activeSessionsByConnection[metrics.connectionId];
        }
    };

    /**
     * Records a latency sample for the given connectionId or sessionId.
     *
     * @param {String} connectionIdOrSessionId
     *     The identifier of the connection (uses current session) or session.
     *
     * @param {Number} latencyMs
     *     The latency measurement in milliseconds.
     */
    service.recordLatency = function recordLatency(connectionIdOrSessionId, latencyMs) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved || latencyMs == null || latencyMs < 0)
            return;

        const metrics = resolved.metrics;
        metrics.latencySamples.push(latencyMs);
        metrics.lastLatencyMs = latencyMs;

        // Track min/max latency
        if (metrics.minLatencyMs == null || latencyMs < metrics.minLatencyMs)
            metrics.minLatencyMs = latencyMs;
        if (metrics.maxLatencyMs == null || latencyMs > metrics.maxLatencyMs)
            metrics.maxLatencyMs = latencyMs;

        // Track latency spikes (>150ms threshold)
        if (latencyMs > 150)
            metrics.latencySpikesCount++;

        // Keep only recent samples (last 100) to avoid memory bloat
        if (metrics.latencySamples.length > 100)
            metrics.latencySamples.shift();

        // Calculate jitter (standard deviation of recent samples)
        // Use last 20 samples for jitter calculation
        const samplesForJitter = metrics.latencySamples.slice(-20);
        if (samplesForJitter.length >= 2) {
            const mean = samplesForJitter.reduce((a, b) => a + b, 0) / samplesForJitter.length;
            const variance = samplesForJitter.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / samplesForJitter.length;
            metrics.jitterMs = Math.round(Math.sqrt(variance) * 10) / 10;
        }
    };

    /**
     * Observes a connection loss event for the given connectionId or sessionId.
     * This is called passively when Guacamole's tunnel indicates a connection loss occurred.
     * Only records metrics - does not alter connection lifecycle.
     *
     * @param {String} connectionIdOrSessionId
     *     The identifier of the connection (uses current session) or session that lost connection.
     */
    service.recordConnectionLoss = function recordConnectionLoss(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return;

        // Only record if session hasn't ended yet (to avoid counting after teardown)
        if (!resolved.metrics.sessionEndTime) {
            resolved.metrics.connectionLossesCount++;
        }
    };

    /**
     * Observes a disconnection event for the given connectionId or sessionId.
     * This is called passively when Guacamole's session tracking indicates a disconnection occurred.
     * Only records metrics - does not alter session lifecycle.
     *
     * @param {String} connectionIdOrSessionId
     *     The identifier of the connection (uses current session) or session that disconnected.
     */
    service.recordDisconnection = function recordDisconnection(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return;

        // Only record if session hasn't ended yet (to avoid counting after teardown)
        if (!resolved.metrics.sessionEndTime) {
            resolved.metrics.disconnectionCount++;
        }
    };

    /**
     * Records that a NOP message was sent (for packet loss calculation).
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     */
    service.recordNopSent = function recordNopSent(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return;

        resolved.metrics.nopMessagesSent++;
        // Recalculate packet loss
        service._calculatePacketLoss(resolved.sessionId);
    };

    /**
     * Records that a sync response was received (for packet loss calculation).
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     */
    service.recordSyncReceived = function recordSyncReceived(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return;

        resolved.metrics.syncResponsesReceived++;
        // Recalculate packet loss
        service._calculatePacketLoss(resolved.sessionId);
    };

    /**
     * Calculates packet loss percentage based on NOP messages sent vs
     * sync responses received.
     *
     * @private
     * @param {String} connectionId
     *     The identifier of the connection.
     */
    service._calculatePacketLoss = function _calculatePacketLoss(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return;

        const metrics = resolved.metrics;
        if (metrics.nopMessagesSent > 0) {
            const lost = metrics.nopMessagesSent - metrics.syncResponsesReceived;
            metrics.packetLossPercent = Math.max(0, Math.round((lost / metrics.nopMessagesSent) * 1000) / 10);
        }
    };

    /**
     * Gets the current latency (last recorded sample) for the given
     * connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The last recorded latency in milliseconds, or null if unavailable.
     */
    service.getCurrentLatency = function getCurrentLatency(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        return resolved.metrics.lastLatencyMs;
    };

    /**
     * Gets the average latency for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The average latency in milliseconds, or null if unavailable.
     */
    service.getAverageLatency = function getAverageLatency(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        const samples = resolved.metrics.latencySamples;
        if (samples.length === 0)
            return null;

        const sum = samples.reduce((a, b) => a + b, 0);
        return Math.round(sum / samples.length);
    };

    /**
     * Gets the connection loss count for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The number of connection losses, or 0 if unavailable.
     */
    service.getConnectionLossCount = function getConnectionLossCount(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        return resolved.metrics.connectionLossesCount;
    };

    /**
     * Gets the unique session ID for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {String}
     *     The unique session identifier, or null if unavailable.
     */
    service.getSessionId = function getSessionId(connectionIdOrSessionId) {
        // If it's already a sessionId, return it
        if (connectionIdOrSessionId && sessionMetrics[connectionIdOrSessionId]) {
            return connectionIdOrSessionId;
        }
        
        // Otherwise, try to resolve as connectionId
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        return resolved.metrics.sessionId || resolved.sessionId;
    };

    /**
     * Gets the session start time for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {String}
     *     The session start time as an ISO string, or null if unavailable.
     */
    service.getSessionStartTime = function getSessionStartTime(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        return resolved.metrics.sessionStartTime;
    };

    /**
     * Gets the session end time for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {String}
     *     The session end time as an ISO string, or null if the session
     *     is still active.
     */
    service.getSessionEndTime = function getSessionEndTime(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        return resolved.metrics.sessionEndTime;
    };

    /**
     * Posts the session summary for the given connectionId to the
     * configured telemetry endpoint. This should be called when a session
     * ends (on disconnect or client teardown).
     *
     * @param {String} connectionId
     *     The identifier of the connection whose session summary should
     *     be posted.
     *
     * @returns {Promise}
     *     A promise that resolves when the POST completes (or fails).
     */
    service.postSessionSummary = function postSessionSummary(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return Promise.resolve();

        const metrics = resolved.metrics;

        // Ensure session has ended and average latency is calculated
        if (!metrics.sessionEndTime) {
            service.endSession(resolved.sessionId);
        }

        const payload = {
            sessionId: metrics.sessionId || metrics.connectionId,  // Include unique sessionId
            connectionId: metrics.connectionId,
            sessionStartTime: metrics.sessionStartTime,
            sessionEndTime: metrics.sessionEndTime,
            connectionLossesCount: metrics.connectionLossesCount,
            avgLatencyMs: metrics.avgLatencyMs
        };

        // Send POST request; ignore any errors (no UI impact)
        return fetch(QINAYA_SESSION_SUMMARY_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        }).catch(function ignoreError() {
            // Silently ignore errors
        });
    };

    /**
     * Cleans up all tracking data for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection to clean up.
     */
    service.cleanup = function cleanup(connectionIdOrSessionId) {
        if (!connectionIdOrSessionId)
            return;

        var resolved = resolveSession(connectionIdOrSessionId);
        if (resolved) {
            delete sessionMetrics[resolved.sessionId];
            // Also clean up active session mapping if this was the active session
            if (activeSessionsByConnection[resolved.metrics.connectionId] === resolved.sessionId) {
                delete activeSessionsByConnection[resolved.metrics.connectionId];
            }
        }
        
        // Also clean up connection-level data (failures)
        delete failureCounts[connectionIdOrSessionId];
        delete failureNotificationSent[connectionIdOrSessionId];
    };

    /**
     * Records bytes sent for bandwidth tracking.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @param {Number} bytes
     *     The number of bytes sent.
     */
    service.recordBytesSent = function recordBytesSent(connectionIdOrSessionId, bytes) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return;
            
        if (bytes == null || bytes < 0 || isNaN(bytes) || bytes === 0)
            return; // Don't record zero bytes

        var metrics = resolved.metrics;
        metrics.bytesSent = (metrics.bytesSent || 0) + bytes;
        
        // Track for rate calculation (sliding window)
        var now = Date.now();
        if (!metrics.sentBytesWindow) {
            metrics.sentBytesWindow = [];
        }
        metrics.sentBytesWindow.push({ timestamp: now, bytes: bytes });
        
        // Remove entries older than 1 second
        var cutoff = now - 1000;
        while (metrics.sentBytesWindow.length > 0 && metrics.sentBytesWindow[0].timestamp < cutoff) {
            metrics.sentBytesWindow.shift();
        }
    };

    /**
     * Records bytes received for bandwidth tracking.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @param {Number} bytes
     *     The number of bytes received.
     */
    service.recordBytesReceived = function recordBytesReceived(connectionIdOrSessionId, bytes) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return;
            
        if (bytes == null || bytes < 0 || isNaN(bytes) || bytes === 0)
            return; // Don't record zero bytes

        var metrics = resolved.metrics;
        metrics.bytesReceived = (metrics.bytesReceived || 0) + bytes;
        
        // Track for rate calculation (sliding window)
        var now = Date.now();
        if (!metrics.receivedBytesWindow) {
            metrics.receivedBytesWindow = [];
        }
        metrics.receivedBytesWindow.push({ timestamp: now, bytes: bytes });
        
        // Remove entries older than 1 second
        var cutoff = now - 1000;
        while (metrics.receivedBytesWindow.length > 0 && metrics.receivedBytesWindow[0].timestamp < cutoff) {
            metrics.receivedBytesWindow.shift();
        }
    };

    /**
     * Gets the total bytes sent for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The total bytes sent, or 0 if unavailable.
     */
    service.getBytesSent = function getBytesSent(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        return resolved.metrics.bytesSent || 0;
    };

    /**
     * Gets the total bytes received for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The total bytes received, or 0 if unavailable.
     */
    service.getBytesReceived = function getBytesReceived(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        return resolved.metrics.bytesReceived || 0;
    };

    /**
     * Gets the total network usage (bytes sent + bytes received) for the
     * given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The total network usage in bytes, or 0 if unavailable.
     */
    service.getTotalNetworkUsage = function getTotalNetworkUsage(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        return (resolved.metrics.bytesSent || 0) + (resolved.metrics.bytesReceived || 0);
    };

    /**
     * Gets the current upload speed (bytes sent per second) in Kbps for the
     * given connectionId. This is calculated from bytes sent in the last second.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The upload speed in Kbps, or 0 if unavailable.
     */
    service.getUploadSpeedKbps = function getUploadSpeedKbps(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        var metrics = resolved.metrics;
        var now = Date.now();
        var cutoff = now - 1000;
        
        // Sum bytes from last second
        var bytesInLastSecond = 0;
        for (var i = 0; i < metrics.sentBytesWindow.length; i++) {
            if (metrics.sentBytesWindow[i].timestamp >= cutoff) {
                bytesInLastSecond += metrics.sentBytesWindow[i].bytes;
            }
        }
        
        // Convert bytes per second to kilobits per second
        // 1 byte = 8 bits, 1 kilobit = 1000 bits
        return Math.round((bytesInLastSecond * 8) / 1000 * 10) / 10;
    };

    /**
     * Gets the current download speed (bytes received per second) in Kbps for
     * the given connectionId. This is calculated from bytes received in the last second.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The download speed in Kbps, or 0 if unavailable.
     */
    service.getDownloadSpeedKbps = function getDownloadSpeedKbps(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        var metrics = resolved.metrics;
        var now = Date.now();
        var cutoff = now - 1000;
        
        // Sum bytes from last second
        var bytesInLastSecond = 0;
        for (var i = 0; i < metrics.receivedBytesWindow.length; i++) {
            if (metrics.receivedBytesWindow[i].timestamp >= cutoff) {
                bytesInLastSecond += metrics.receivedBytesWindow[i].bytes;
            }
        }
        
        // Convert bytes per second to kilobits per second
        // 1 byte = 8 bits, 1 kilobit = 1000 bits
        return Math.round((bytesInLastSecond * 8) / 1000 * 10) / 10;
    };

    /**
     * Gets the minimum latency observed for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The minimum latency in milliseconds, or null if unavailable.
     */
    service.getMinLatency = function getMinLatency(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        return resolved.metrics.minLatencyMs;
    };

    /**
     * Gets the maximum latency observed for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The maximum latency in milliseconds, or null if unavailable.
     */
    service.getMaxLatency = function getMaxLatency(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        return resolved.metrics.maxLatencyMs;
    };

    /**
     * Gets the jitter (standard deviation of latency) for the given connectionId.
     * Recalculates jitter from recent samples if we have enough data.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The jitter in milliseconds, or null if unavailable.
     */
    service.getJitter = function getJitter(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        var metrics = resolved.metrics;
        
        // Recalculate jitter if we have at least 2 samples
        var samplesForJitter = metrics.latencySamples.slice(-20);
        if (samplesForJitter.length >= 2) {
            var mean = samplesForJitter.reduce(function(a, b) { return a + b; }, 0) / samplesForJitter.length;
            var variance = samplesForJitter.reduce(function(sum, val) {
                return sum + Math.pow(val - mean, 2);
            }, 0) / samplesForJitter.length;
            metrics.jitterMs = Math.round(Math.sqrt(variance) * 10) / 10;
        }

        return metrics.jitterMs;
    };

    /**
     * Gets the count of latency spikes (>150ms) for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The count of latency spikes, or 0 if unavailable.
     */
    service.getLatencySpikesCount = function getLatencySpikesCount(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        return resolved.metrics.latencySpikesCount || 0;
    };

    /**
     * Gets the disconnection count for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The disconnection count, or 0 if unavailable.
     */
    service.getDisconnectionCount = function getDisconnectionCount(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return 0;

        return resolved.metrics.disconnectionCount || 0;
    };

    /**
     * Gets the packet loss percentage for the given connectionId.
     *
     * @param {String} connectionId
     *     The identifier of the connection.
     *
     * @returns {Number}
     *     The packet loss percentage (0-100), or null if unavailable.
     */
    service.getPacketLossPercent = function getPacketLossPercent(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        return resolved.metrics.packetLossPercent;
    };

    /**
     * Gets a provisional live connection quality rating using
     * TELEMETRY_RATING_FORMULA_VERSION (backend-penalty-v1). This mirrors the
     * backend penalty model in TelemetryManager and uses the same score
     * thresholds. Backend persisted rating remains canonical.
     *
     * @param {String} connectionIdOrSessionId
     *     The identifier of the connection (uses current session) or session.
     *
     * @returns {Object}
     *     An object with 'rating' (string: Excellent/Good/Fair/Poor/Bad) and
     *     'score' (number: 0-100), or null if unavailable.
     */
    service.getConnectionRating = function getConnectionRating(connectionIdOrSessionId) {
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved)
            return null;

        var metrics = resolved.metrics;
        var score = 100;

        // Mirror backend penalty model (TelemetryManager.FORMULA_VERSION)
        var avgLatency = metrics.avgLatencyMs != null ? metrics.avgLatencyMs
                : (metrics.lastLatencyMs != null ? metrics.lastLatencyMs : null);
        if (avgLatency != null) {
            if (avgLatency > 200)
                score -= 30;
            else if (avgLatency > 100)
                score -= 15;
            else if (avgLatency > 50)
                score -= 5;
        }

        var avgJitter = metrics.jitterMs != null ? metrics.jitterMs : null;
        if (avgJitter != null) {
            if (avgJitter > 50)
                score -= 20;
            else if (avgJitter > 20)
                score -= 10;
        }

        var packetLoss = metrics.packetLossPercent != null ? metrics.packetLossPercent : null;
        if (packetLoss != null) {
            if (packetLoss > 5.0)
                score -= 25;
            else if (packetLoss > 2.0)
                score -= 15;
            else if (packetLoss > 0.5)
                score -= 5;
        }

        score -= (metrics.connectionLossesCount || 0) * 15;
        score -= (metrics.disconnectionCount || 0) * 10;

        score = Math.max(0, Math.min(100, Math.round(score)));

        // Backend canonical thresholds
        var rating;
        if (score >= 90)
            rating = 'Excellent';
        else if (score >= 70)
            rating = 'Good';
        else if (score >= 50)
            rating = 'Fair';
        else if (score >= 30)
            rating = 'Poor';
        else
            rating = 'Bad';

        return {
            rating: rating,
            score: score
        };
    };

    /**
     * Synchronizes the telemetry session data for the given connectionId or sessionId
     * to the backend API.
     *
     * @param {String} connectionIdOrSessionId
     *     The identifier of the connection (uses current session) or session whose telemetry should be synced.
     */
    service.syncSessionToBackend = function syncSessionToBackend(connectionIdOrSessionId, _attempt) {
        if (!connectionIdOrSessionId) {
            console.warn('[Telemetry] syncSessionToBackend called without connectionIdOrSessionId');
            return;
        }

        // Bounded client-side retry on transient (5xx / network) failures.
        var attempt = _attempt || 0;
        var MAX_SYNC_ATTEMPTS = 3;

        // Normalizes a value to a finite number or null so the JSON payload
        // never carries NaN or Infinity (the strict receiver expects numbers).
        var num = function num(v) {
            return (typeof v === 'number' && isFinite(v)) ? v : null;
        };
        
        if (!authenticationService) {
            console.warn('[Telemetry] syncSessionToBackend called but authenticationService is not available');
            return;
        }

        telemetryDebugLog('[Telemetry] syncSessionToBackend called with:', connectionIdOrSessionId, '| Available sessions:', Object.keys(sessionMetrics).length, '| Active sessions:', Object.keys(activeSessionsByConnection).length);
        
        var resolved = resolveSession(connectionIdOrSessionId);
        if (!resolved) {
            console.warn('[Telemetry] Could not resolve session for:', connectionIdOrSessionId, '| Available sessionIds:', Object.keys(sessionMetrics), '| Active sessions by connection:', activeSessionsByConnection);
            return;
        }

        var metrics = resolved.metrics;
        var rating = service.getConnectionRating(resolved.sessionId);

        // Calculate averages on the fly (not just on session end)
        var avgLatency = service.getAverageLatency(resolved.sessionId);
        var jitter = service.getJitter(resolved.sessionId);
        var uploadSpeed = service.getUploadSpeedKbps(resolved.sessionId);
        var downloadSpeed = service.getDownloadSpeedKbps(resolved.sessionId);
        
        // Update stored values if calculated
        if (avgLatency != null) {
            metrics.avgLatencyMs = avgLatency;
        }
        if (jitter != null && metrics.jitterMs == null) {
            metrics.jitterMs = jitter;
        }

        // Get current username from authentication service
        var currentUsername = null;
        try {
            if (authenticationService && typeof authenticationService.getCurrentUsername === 'function') {
                currentUsername = authenticationService.getCurrentUsername();
            }
        } catch (e) {
            console.warn('[Telemetry] Could not get username:', e.message);
        }

        // Build telemetry session object. Prefer tunnel UUID as sessionId when
        // known so backend session-first lookup matches the canonical Mirage ID.
        // metrics.sessionId remains a provisional local key until then.
        var canonicalSessionId = metrics.tunnelUuid || metrics.sessionId || resolved.sessionId;
        var telemetrySession = {
            sessionId: canonicalSessionId,
            connectionId: metrics.connectionId,  // Grouping key only; not unique per session
            username: currentUsername || '',
            sessionStartTime: metrics.sessionStartTime ? new Date(metrics.sessionStartTime).toISOString() : null,
            sessionEndTime: metrics.sessionEndTime ? new Date(metrics.sessionEndTime).toISOString() : null,
            avgLatencyMs: num(avgLatency),
            minLatencyMs: num(metrics.minLatencyMs),
            maxLatencyMs: num(metrics.maxLatencyMs),
            currentLatencyMs: num(metrics.lastLatencyMs),
            avgJitterMs: num(jitter),
            latencySpikesCount: num(metrics.latencySpikesCount) || 0,
            packetLossPercent: num(metrics.packetLossPercent),
            connectionLossesCount: num(metrics.connectionLossesCount) || 0,
            disconnectionCount: num(metrics.disconnectionCount) || 0,
            connectionRating: rating ? rating.rating : null,
            connectionRatingScore: rating ? num(rating.score) : null,
            totalBytesSent: num(metrics.bytesSent) || 0,
            totalBytesReceived: num(metrics.bytesReceived) || 0,
            avgUploadSpeedKbps: uploadSpeed > 0 ? num(uploadSpeed) : null,
            avgDownloadSpeedKbps: downloadSpeed > 0 ? num(downloadSpeed) : null
        };

        // Validate that we have a sessionId - this is critical
        if (!telemetrySession.sessionId || telemetrySession.sessionId === '') {
            console.error('[Telemetry] ❌ CRITICAL: Cannot sync - telemetrySession.sessionId is missing or empty!', telemetrySession);
            return;
        }
        
        // Send to backend API
        telemetryDebugLog('[Telemetry] Syncing to backend - connectionId:', metrics.connectionId, 'sessionId:', telemetrySession.sessionId);
        telemetryDebugLog('[Telemetry] Session data summary - startTime:', telemetrySession.sessionStartTime, 'endTime:', telemetrySession.sessionEndTime, 'bytesSent:', telemetrySession.totalBytesSent, 'bytesReceived:', telemetrySession.totalBytesReceived);
        telemetryDebugLog('[Telemetry] Full session data:', JSON.stringify(telemetrySession, null, 2));
        
        // Check if authenticationService.request exists
        if (!authenticationService || typeof authenticationService.request !== 'function') {
            console.error('[Telemetry] authenticationService.request is not available!', authenticationService);
            return;
        }
        
        try {
            var requestPromise = authenticationService.request({
                method: 'POST',
                url: 'api/telemetry/sessions',
                data: telemetrySession
            });
            
            if (!requestPromise || typeof requestPromise.then !== 'function') {
                console.error('[Telemetry] authenticationService.request did not return a promise!', requestPromise);
                return;
            }
            
            requestPromise.then(function(response) {
                // The backend now returns a structured result. Only treat the
                // sync as successful when the backend confirms it durably
                // accepted (and, for active sessions, queued) the telemetry.
                // A 2xx alone does not prove durable handling.
                var body = (response && response.data) ? response.data : response;
                var accepted = !!(body && body.accepted);
                if (accepted) {
                    telemetryDebugLog('[Telemetry] ✅ Backend accepted telemetry - connectionId:',
                        metrics.connectionId, 'sessionId:', telemetrySession.sessionId,
                        'exportState:', body && body.exportState, 'exportQueued:', body && body.exportQueued);
                }
                else {
                    console.warn('[Telemetry] ⚠️ Backend did not durably accept telemetry - connectionId:',
                        metrics.connectionId, 'sessionId:', telemetrySession.sessionId,
                        'response:', body);
                }
            }).catch(function(error) {
                var status = (error && typeof error.status === 'number') ? error.status : 0;

                // Retry only transient failures (network / 5xx) with bounded backoff.
                var transient = (status === 0 || status >= 500);
                if (transient && attempt + 1 < MAX_SYNC_ATTEMPTS) {
                    var delayMs = Math.pow(2, attempt) * 1000;
                    console.warn('[Telemetry] Transient sync failure (status ' + status + '); retrying in '
                        + delayMs + 'ms - connectionId:', metrics.connectionId, 'sessionId:', telemetrySession.sessionId);
                    setTimeout(function() {
                        service.syncSessionToBackend(connectionIdOrSessionId, attempt + 1);
                    }, delayMs);
                    return;
                }

                // Non-retriable, or retries exhausted. Telemetry is non-critical
                // but persistent failures are logged for diagnosis.
                console.error('[Telemetry] ❌ Failed to sync to backend - connectionId:', metrics.connectionId, 'sessionId:', telemetrySession.sessionId);
                console.error('[Telemetry] Error status:', status, '| attempts:', attempt + 1);
                if (error && error.data) {
                    console.error('[Telemetry] Error data:', JSON.stringify(error.data, null, 2));
                }
            });
        } catch (e) {
            console.error('[Telemetry] ❌ Exception while calling authenticationService.request:', e);
            console.error('[Telemetry] Exception message:', e && e.message);
            console.error('[Telemetry] Exception stack:', e && e.stack);
        }
    };

    /**
     * Attaches telemetry observation to Guacamole's native client and tunnel events.
     * This hooks into Guacamole.Tunnel.onuuid and Guacamole.Tunnel.oninstruction to observe
     * the native session lifecycle and use Guacamole's built-in ping mechanism for latency.
     * 
     * This method preserves any existing event handlers to ensure non-intrusive observation.
     * 
     * @param {Guacamole.Client} guacClient
     *     The Guacamole client instance to observe.
     * 
     * @param {String} connectionId
     *     The application-level connection identifier associated with this client.
     * 
     * @returns {Function}
     *     A cleanup function that detaches the telemetry observers when called.
     */
    service.attachToGuacamoleClient = function attachToGuacamoleClient(guacClient, connectionId) {
        if (!guacClient || !connectionId) {
            console.warn('[Telemetry] attachToGuacamoleClient called without client or connectionId');
            return function() {};
        }

        var tunnel = guacClient.tunnel;
        if (!tunnel) {
            console.warn('[Telemetry] attachToGuacamoleClient called but client has no tunnel');
            return function() {};
        }

        telemetryDebugLog('[Telemetry] Attaching to Guacamole client for connectionId:', connectionId);

        // Preserve existing handlers
        var origTunnelOnUuid = tunnel.onuuid;
        var origTunnelOnInstruction = tunnel.oninstruction;

        // Hook into tunnel UUID event to observe Guacamole's native session identifier
        tunnel.onuuid = function(uuid) {
            // Call original handler first
            if (origTunnelOnUuid && typeof origTunnelOnUuid === 'function') {
                origTunnelOnUuid.call(tunnel, uuid);
            }
            
            // Observe the tunnel UUID assignment
            service.observeTunnelUuid(connectionId, uuid);
        };

        // Hook into tunnel instruction handler to observe native ping responses
        tunnel.oninstruction = function(opcode, parameters) {
            // Call original handler first to preserve normal processing
            if (origTunnelOnInstruction && typeof origTunnelOnInstruction === 'function') {
                origTunnelOnInstruction.call(tunnel, opcode, parameters);
            }

            // Check for Guacamole's internal ping response (WebSocket tunnel)
            // The internal data opcode is an empty string in Guacamole
            if (opcode === '' && parameters && parameters.length >= 2 && parameters[0] === 'ping') {
                // Expected format: ["ping", "<timestamp>"]
                var sentTimestamp = parseInt(parameters[1], 10);
                if (sentTimestamp && !isNaN(sentTimestamp)) {
                    service.observeNativePing(connectionId, sentTimestamp);
                }
            }
        };

        // Return cleanup function to restore original handlers
        return function detachTelemetry() {
            telemetryDebugLog('[Telemetry] Detaching from Guacamole client for connectionId:', connectionId);
            tunnel.onuuid = origTunnelOnUuid;
            tunnel.oninstruction = origTunnelOnInstruction;
        };
    };

    /**
     * Starts native Guacamole ping interval for a session.
     * This sends periodic pings using Guacamole's internal mechanism to measure latency.
     * 
     * Note: This is only applicable for WebSocket tunnels. HTTP tunnels don't support
     * native ping/pong in the same way.
     *
     * @param {Guacamole.Client} guacClient
     *     The Guacamole client instance.
     * 
     * @param {String} connectionId
     *     The connection identifier.
     * 
     * @param {Number} intervalMs
     *     The interval in milliseconds between pings (default: 1000ms).
     * 
     * @returns {Function}
     *     A cleanup function that stops the ping interval when called.
     */
    service.startNativePingInterval = function startNativePingInterval(guacClient, connectionId, intervalMs) {
        if (!guacClient || !connectionId) {
            console.warn('[Telemetry] startNativePingInterval called without client or connectionId');
            return function() {};
        }

        var interval = intervalMs || 1000;
        var tunnel = guacClient.tunnel;
        
        if (!tunnel) {
            console.warn('[Telemetry] startNativePingInterval called but client has no tunnel');
            return function() {};
        }

        telemetryDebugLog('[Telemetry] Starting native ping interval for connectionId:', connectionId, 'interval:', interval, 'ms');

        // Start interval to send pings
        var pingIntervalId = setInterval(function() {
            // Only send ping if tunnel is open
            if (tunnel && tunnel.state === 2) { // Guacamole.Tunnel.State.OPEN = 2
                try {
                    // Send internal ping with timestamp
                    // Format: tunnel.sendMessage(opcode, ...parameters)
                    // Internal opcode is empty string ''
                    var now = Date.now();
                    tunnel.sendMessage('', 'ping', now.toString());
                    
                    if (window.GUAC_DEBUG_LATENCY) {
                        telemetryDebugLog('[Telemetry] Sent native ping at:', now);
                    }
                } catch (e) {
                    console.error('[Telemetry] Error sending native ping:', e);
                }
            }
        }, interval);

        // Return cleanup function to stop the interval
        return function stopPingInterval() {
            telemetryDebugLog('[Telemetry] Stopping native ping interval for connectionId:', connectionId);
            clearInterval(pingIntervalId);
        };
    };

    return service;

}]);

